import React, { useState } from "react";
import {
    ShieldCheck, ShieldAlert, ShieldX, CheckCircle2, AlertTriangle,
    XCircle, Loader2, RotateCw, ChevronDown,
} from "lucide-react";
import { api } from "@/lib/api";

/* ─────────────────────────────────────────────────────────────────────────
   PreflightCard — Phase 2 launch validator UI.

   Calls POST /api/strategy/preflight and shows a traffic-light result
   (green / yellow / red) over the current strategy BEFORE launch:
     green  → all 7 checks passed, safe to launch
     yellow → launchable but with warnings the user should review
     red    → hard blockers; launch should be gated

   Backend contract (backend/preflight.py → run_preflight):
     {
       preflight_status: "green"|"yellow"|"red",
       can_launch: bool,
       checks: [{ id, status, detail, items }],
       blockers: [str], warnings: [str], summary: str
     }

   Props:
     onResult(result)  — optional callback so the parent can gate its
                         launch button on result.can_launch.
   ───────────────────────────────────────────────────────────────────────── */

const CHECK_LABEL = {
    structure: "Структура кампаній",
    geo_lang_ids: "Гео та мова",
    bid_micros: "Ставки (bids)",
    budget_vs_bid: "Бюджет vs ставка",
    conversion_actions: "Відстеження конверсій",
    char_limits: "Ліміти символів",
    landing_pages: "Цільові сторінки",
    input: "Стратегія",
};

const STATUS_META = {
    green: {
        Icon: ShieldCheck, badgeIcon: CheckCircle2,
        text: "text-success-c", bg: "border-success/20 bg-success/5",
        dot: "bg-success", label: "Готово до запуску",
    },
    yellow: {
        Icon: ShieldAlert, badgeIcon: AlertTriangle,
        text: "text-warning-c", bg: "border-warning/30 bg-warning/8",
        dot: "bg-warning", label: "Можна запускати — є попередження",
    },
    red: {
        Icon: ShieldX, badgeIcon: XCircle,
        text: "text-error-c", bg: "border-error/30 bg-error/8",
        dot: "bg-error", label: "Знайдено блокери",
    },
};

const ROW_ICON = {
    green: { Icon: CheckCircle2, cls: "text-success-c" },
    yellow: { Icon: AlertTriangle, cls: "text-warning-c" },
    red: { Icon: XCircle, cls: "text-error-c" },
};

export default function PreflightCard({ onResult }) {
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState("");
    const [expanded, setExpanded] = useState(true);

    const run = async () => {
        setLoading(true);
        setError("");
        try {
            const r = await api.post("/strategy/preflight");
            setResult(r.data || r);
            if (onResult) onResult(r.data || r);
        } catch (e) {
            const msg =
                e?.response?.data?.detail ||
                e?.message ||
                "Не вдалося виконати перевірку. Спробуйте ще раз.";
            setError(typeof msg === "string" ? msg : "Помилка перевірки");
        } finally {
            setLoading(false);
        }
    };

    const meta = result ? (STATUS_META[result.preflight_status] || STATUS_META.red) : null;

    return (
        <section
            className="rounded-xl border border-border bg-surface p-5"
            data-testid="preflight-card"
        >
            {/* Header */}
            <div className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-surface-hover flex items-center justify-center shrink-0">
                        {meta ? (
                            <meta.Icon size={18} className={meta.text} />
                        ) : (
                            <ShieldCheck size={18} className="text-secondary-c" />
                        )}
                    </div>
                    <div>
                        <h3 className="text-text-primary font-semibold text-sm">
                            Перевірка готовності
                        </h3>
                        <p className="text-secondary-c text-xs mt-0.5">
                            7 автоматичних перевірок перед запуском
                        </p>
                    </div>
                </div>

                <button
                    onClick={run}
                    disabled={loading}
                    className="shrink-0 inline-flex items-center gap-1.5 text-xs font-mono px-3 py-2 rounded-md border border-border text-secondary-c hover:border-primary/40 hover:text-text-primary transition disabled:opacity-50"
                    data-testid="preflight-run"
                >
                    {loading ? (
                        <><Loader2 size={13} className="animate-spin" /> Перевіряю…</>
                    ) : result ? (
                        <><RotateCw size={13} /> Перевірити знову</>
                    ) : (
                        <><ShieldCheck size={13} /> Перевірити</>
                    )}
                </button>
            </div>

            {/* Error */}
            {error && (
                <div className="mt-4 rounded-lg border border-error/30 bg-error/8 p-3 text-xs text-error-c" data-testid="preflight-error">
                    {error}
                </div>
            )}

            {/* Result */}
            {result && meta && (
                <div className="mt-4">
                    {/* Summary banner */}
                    <div className={`rounded-lg border p-3 flex items-center gap-2.5 ${meta.bg}`} data-testid="preflight-summary">
                        <span className={`w-2 h-2 rounded-full shrink-0 ${meta.dot}`} />
                        <div className="flex-1">
                            <div className={`text-xs font-semibold ${meta.text}`}>{meta.label}</div>
                            <div className="text-secondary-c text-xs mt-0.5">{result.summary}</div>
                        </div>
                    </div>

                    {/* Per-check rows */}
                    <button
                        onClick={() => setExpanded(v => !v)}
                        className="mt-3 w-full flex items-center justify-between text-secondary-c text-xs hover:text-text-primary transition-colors"
                        data-testid="preflight-toggle"
                    >
                        <span>Деталі перевірок ({result.checks?.length || 0})</span>
                        <ChevronDown size={14} className={`transition-transform ${expanded ? "rotate-180" : ""}`} />
                    </button>

                    {expanded && (
                        <ul className="mt-2 space-y-1.5" data-testid="preflight-checks">
                            {(result.checks || []).map((c, i) => {
                                const ri = ROW_ICON[c.status] || ROW_ICON.red;
                                return (
                                    <li
                                        key={c.id || i}
                                        className="flex items-start gap-2.5 rounded-lg bg-surface-hover/40 px-3 py-2"
                                        data-testid={`preflight-check-${c.id}`}
                                    >
                                        <ri.Icon size={15} className={`${ri.cls} shrink-0 mt-0.5`} />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-text-primary text-xs font-medium">
                                                {CHECK_LABEL[c.id] || c.id}
                                            </div>
                                            <div className="text-secondary-c text-xs mt-0.5 break-words">
                                                {c.detail}
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            )}
        </section>
    );
}
